import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, CircularProgress, Card, CardContent, CardActions,
  Button, Modal, TextField, IconButton, Avatar, List, ListItem, ListItemAvatar,
  ListItemText, Paper
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Favorite, FavoriteBorder, Comment, Edit, Delete, Send, Add, ThumbUp, InfoOutlined
} from '@mui/icons-material';
import { useAuth } from '../components/auth/AuthContext';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SchoolInteractions = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { schoolId } = useAuth();
  const navigate = useNavigate();
  const [editingPost, setEditingPost] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [newComment, setNewComment] = useState('');
  const [activeCommentSections, setActiveCommentSections] = useState({});
  const [commentLoading, setCommentLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const commentInputRef = useRef(null);

  const fetchPosts = async () => {
    try {
      const response = await axios.get('https://kidsm.vercel.app/api/schools/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPosts();
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post('https://kidsm.vercel.app/api/schools/posts/like', { postId, schoolId });
      if (response.status === 200) {
        setPosts(posts.map(post => post._id === postId ? { ...post, likes: response.data.likes } : post));
        if (selectedPost && selectedPost._id === postId) {
          setSelectedPost({ ...selectedPost, likes: response.data.likes });
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleCommentLike = async (postId, commentId) => {
    try {
      const response = await axios.post('https://kidsm.vercel.app/api/schools/posts/comment/like', { postId, commentId, schoolId });
      if (response.status === 200) {
        setPosts(posts.map(post => {
          if (post._id === postId) {
            const updatedComments = post.comments.map(comment =>
              comment._id === commentId ? response.data.comment : comment
            );
            return { ...post, comments: updatedComments };
          }
          return post;
        }));
        if (selectedPost && selectedPost._id === postId) {
          const updatedComments = selectedPost.comments.map(comment =>
            comment._id === commentId ? response.data.comment : comment
          );
          setSelectedPost({ ...selectedPost, comments: updatedComments });
        }
      }
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const handleAddComment = async (postId) => {
    if (!newComment.trim()) return;
    setCommentLoading(true);
    try {
      const response = await axios.post('https://kidsm.vercel.app/api/schools/posts/comment', { postId, schoolId, content: newComment });
      if (response.status === 201) {
        setPosts(posts.map(post => post._id === postId ? response.data.post : post));
        if (selectedPost && selectedPost._id === postId) {
          setSelectedPost(response.data.post);
        }
        setNewComment('');
        if (commentInputRef.current) {
          setTimeout(() => commentInputRef.current.focus(), 100);
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      const response = await axios.delete('https://kidsm.vercel.app/api/schools/posts/comment/delete', { data: { postId, commentId, schoolId } });
      if (response.status === 200) {
        setPosts(posts.map(post => {
          if (post._id === postId) {
            return { ...post, comments: post.comments.filter(c => c._id !== commentId) };
          }
          return post;
        }));
        if (selectedPost && selectedPost._id === postId) {
          setSelectedPost({ ...selectedPost, comments: selectedPost.comments.filter(c => c._id !== commentId) });
        }
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  const toggleComments = (postId) => {
    setActiveCommentSections(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditTitle(post.title);
    setEditDescription(post.description);
  };

  const handleUpdatePost = async () => {
    try {
      const formData = new FormData();
      formData.append('postId', editingPost._id);
      formData.append('schoolId', schoolId);
      formData.append('title', editTitle);
      formData.append('description', editDescription);
      const response = await axios.put('https://kidsm.vercel.app/api/schools/posts/update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.status === 200) {
        setPosts(posts.map(post => post._id === editingPost._id ? response.data.post : post));
        if (selectedPost && selectedPost._id === editingPost._id) {
          setSelectedPost(response.data.post);
        }
        setEditingPost(null);
      }
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      const response = await axios.delete('https://kidsm.vercel.app/api/schools/posts/delete', { data: { postId, schoolId } });
      if (response.status === 200) {
        setPosts(posts.filter(post => post._id !== postId));
        if (selectedPost && selectedPost._id === postId) {
          setDetailModalOpen(false);
          setSelectedPost(null);
        }
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const openPostDetails = (post) => {
    setSelectedPost(post);
    setDetailModalOpen(true);
    setNewComment('');
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const renderComment = (comment, postId) => (
    <ListItem key={comment._id} sx={{ bgcolor: '#f9f9f9', borderRadius: 2, mb: 1, p: 2 }}>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: '#6200ee' }}>{comment.schoolId?.schoolName?.charAt(0) || 'U'}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">{comment.schoolId?.schoolName || 'Unknown School'}</Typography>
            {comment.schoolId._id === schoolId && (
              <IconButton size="small" onClick={() => handleDeleteComment(postId, comment._id)}>
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
        secondary={
          <>
            <Typography variant="body2" color="text.secondary">{formatDate(comment.createdAt)}</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>{comment.content}</Typography>
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={<ThumbUp fontSize="small" />}
                color={comment.likes.includes(schoolId) ? 'primary' : 'inherit'}
                onClick={() => handleCommentLike(postId, comment._id)}
              >
                {comment.likes.length > 0 ? comment.likes.length : ''}
              </Button>
            </Box>
          </>
        }
      />
    </ListItem>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }} className="bg-gradient-to-b from-gray-100 to-gray-200">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 4, textAlign: 'center' }}>
          School Interactions
        </Typography>

        <Modal open={!!editingPost} onClose={() => setEditingPost(null)}>
          <Box sx={{ width: '90%', maxWidth: 500, bgcolor: 'white', p: 4, borderRadius: 2, mx: 'auto', mt: '10vh' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#6200ee' }}>Edit Post</Typography>
            <TextField
              fullWidth
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="contained" color="error" onClick={() => setEditingPost(null)}>Cancel</Button>
              <Button variant="contained" color="primary" onClick={handleUpdatePost}>Save Changes</Button>
            </Box>
          </Box>
        </Modal>

        <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
          <Box sx={{ width: '90%', maxWidth: 800, bgcolor: 'white', p: 4, borderRadius: 2, mx: 'auto', mt: '5vh', maxHeight: '85vh', overflowY: 'auto' }}>
            {selectedPost && (
              <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#6200ee', mr: 2 }}>{selectedPost.schoolId.schoolName.charAt(0)}</Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{selectedPost.schoolId.schoolName}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(selectedPost.createdAt)}</Typography>
                    </Box>
                  </Box>
                  {selectedPost.schoolId._id === schoolId && (
                    <Box>
                      <IconButton onClick={() => { setDetailModalOpen(false); handleEditPost(selectedPost); }}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePost(selectedPost._id)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>{selectedPost.title}</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedPost.description}</Typography>
                {selectedPost.images.length > 0 && (
                  <Box sx={{ mb: 2, overflowX: 'auto', whiteSpace: 'nowrap' }}>
                    {selectedPost.images.map((img, index) => (
                      <img key={index} src={img.url} alt="Post" style={{ width: '100%', maxWidth: 400, height: 300, objectFit: 'contain', display: 'inline-block', marginRight: 10, borderRadius: 10 }} />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, borderTop: '1px solid #eee', borderBottom: '1px solid #eee', py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ThumbUp sx={{ mr: 1, color: '#6200ee' }} fontSize="small" />
                    <Typography variant="caption">{selectedPost.likes.length} likes</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Comment sx={{ mr: 1, color: '#6200ee' }} fontSize="small" />
                    <Typography variant="caption">{selectedPost.comments?.length || 0} comments</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                  <Button
                    startIcon={selectedPost.likes.includes(schoolId) ? <Favorite sx={{ color: '#ff4444' }} /> : <FavoriteBorder />}
                    color={selectedPost.likes.includes(schoolId) ? 'error' : 'inherit'}
                    onClick={() => handleLike(selectedPost._id)}
                  >
                    Like
                  </Button>
                  <Button
                    startIcon={<Comment />}
                    onClick={() => commentInputRef.current.focus()}
                  >
                    Comment
                  </Button>
                </Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Comments</Typography>
                {selectedPost.comments?.length > 0 ? (
                  <List>
                    {selectedPost.comments.map(comment => renderComment(comment, selectedPost._id))}
                  </List>
                ) : (
                  <Typography color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>No comments yet</Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, borderTop: '1px solid #eee', pt: 2 }}>
                  <TextField
                    inputRef={commentInputRef}
                    fullWidth
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mr: 2 }}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => handleAddComment(selectedPost._id)}
                    disabled={commentLoading}
                  >
                    {commentLoading ? <CircularProgress size={24} /> : <Send />}
                  </IconButton>
                </Box>
              </motion.div>
            )}
          </Box>
        </Modal>

        <Box sx={{ mx: 2, mt: 2, mb: 4 }}>
          <Button
            variant="contained"
            sx={{
              borderRadius: '25px',
              background: 'linear-gradient(to right, #6200ee, #3700b3)',
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              py: 1.5,
              px: 3,
              boxShadow: '0 2px 4px rgba(98, 0, 238, 0.3)',
              color: '#fff',
            }}
            startIcon={<Add />}
            onClick={() => navigate('/create-post')}
          >
            Create Post
          </Button>
        </Box>

        {posts.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <InfoOutlined sx={{ fontSize: 40, color: '#888' }} />
            <Typography sx={{ mt: 2, color: '#666' }}>
              No posts available. Be the first to post!
            </Typography>
          </Box>
        ) : (
          posts.map(post => (
            <Card key={post._id} sx={{ mb: 3, p: 2, borderRadius: 3, boxShadow: 3, bgcolor: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#6200ee', mr: 2 }}>{post.schoolId.schoolName.charAt(0)}</Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{post.schoolId.schoolName}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(post.createdAt)}</Typography>
                    </Box>
                  </Box>
                  {post.schoolId._id === schoolId && (
                    <Box>
                      <IconButton onClick={() => handleEditPost(post)}><Edit /></IconButton>
                      <IconButton onClick={() => handleDeletePost(post._id)}><Delete /></IconButton>
                    </Box>
                  )}
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>{post.title}</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{post.description}</Typography>
                {post.images.length > 0 && (
                  <Box sx={{ position: 'relative', mb: 2, borderRadius: 2, overflow: 'hidden', height: 200 }}>
                    <img src={post.images[0].url} alt="Post" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {post.images.length > 1 && (
                      <Box sx={{ position: 'absolute', right: 10, bottom: 10, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        +{post.images.length - 1}
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ borderTop: '1px solid #eee', pt: 2 }}>
                <Button
                  startIcon={post.likes.includes(schoolId) ? <Favorite sx={{ color: '#ff4444' }} /> : <FavoriteBorder />}
                  color={post.likes.includes(schoolId) ? 'error' : 'inherit'}
                  onClick={() => handleLike(post._id)}
                >
                  {post.likes.length}
                </Button>
                <Button
                  startIcon={<Comment />}
                  onClick={() => toggleComments(post._id)}
                >
                  {post.comments?.length || 0}
                </Button>
                <Button onClick={() => openPostDetails(post)}>View Details</Button>
              </CardActions>
              {activeCommentSections[post._id] && (
                <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                  {post.comments?.length > 0 ? (
                    <List>
                      {post.comments.slice(0, 2).map(comment => renderComment(comment, post._id))}
                    </List>
                  ) : (
                    <Typography sx={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>No comments yet</Typography>
                  )}
                  {post.comments?.length > 2 && (
                    <Button
                      color="primary"
                      onClick={() => openPostDetails(post)}
                      sx={{ mt: 1 }}
                    >
                      View all {post.comments.length} comments
                    </Button>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Write a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      multiline
                      rows={2}
                      sx={{ mr: 2 }}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => handleAddComment(post._id)}
                      disabled={commentLoading}
                    >
                      {commentLoading ? <CircularProgress size={24} /> : <Send />}
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Card>
          ))
        )}
      </motion.div>
    </Container>
  );
};

export default SchoolInteractions;